import { useState } from 'react';
import { reviews } from '@/mocks/reviews';

const DESKTOP_PAGE_SIZE = 10;
const MOBILE_PAGE_SIZE = 5;

const StarRating = ({ rating }: { rating: number }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <i
        key={star}
        className={`ri-star-fill text-xs ${star <= rating ? 'text-yellow-400' : 'text-gray-200'}`}
      ></i>
    ))}
  </div>
);

const ReviewCard = ({ review }: { review: typeof reviews[0] }) => (
  <div className="bg-white rounded-2xl p-5 border border-gray-100 hover:-translate-y-0.5 transition-transform duration-200">
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-9 h-9 flex items-center justify-center rounded-full ${review.avatarBg} font-bold text-sm text-gray-700 flex-shrink-0`}>
        {review.name[0]}
      </div>
      <div>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-900 text-sm">{review.name}</span>
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${review.ageColor}`}>
            {review.age}
          </span>
        </div>
        <StarRating rating={review.rating} />
      </div>
    </div>
    <p className="text-gray-600 text-sm leading-relaxed">
      &ldquo;{review.content}&rdquo;
    </p>
  </div>
);

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) => {
  const maxVisible = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  const endPage = Math.min(totalPages, startPage + maxVisible - 1);
  if (endPage - startPage + 1 < maxVisible) {
    startPage = Math.max(1, endPage - maxVisible + 1);
  }
  const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

  return (
    <div className="flex items-center justify-center gap-1.5 mt-8">
      <button
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors hover:border-green-400 hover:text-green-600"
      >
        <i className="ri-arrow-left-s-line text-lg"></i>
      </button>

      {startPage > 1 && (
        <>
          <button
            onClick={() => onPageChange(1)}
            className="w-9 h-9 flex items-center justify-center rounded-full text-sm font-bold cursor-pointer transition-all bg-white border border-gray-200 text-gray-500 hover:border-green-400 hover:text-green-600"
          >
            1
          </button>
          {startPage > 2 && <span className="text-gray-400 text-sm px-1">···</span>}
        </>
      )}

      {pages.map((page) => (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-bold cursor-pointer transition-all ${
            currentPage === page
              ? 'bg-green-500 text-white'
              : 'bg-white border border-gray-200 text-gray-500 hover:border-green-400 hover:text-green-600'
          }`}
        >
          {page}
        </button>
      ))}

      {endPage < totalPages && (
        <>
          {endPage < totalPages - 1 && <span className="text-gray-400 text-sm px-1">···</span>}
          <button
            onClick={() => onPageChange(totalPages)}
            className="w-9 h-9 flex items-center justify-center rounded-full text-sm font-bold cursor-pointer transition-all bg-white border border-gray-200 text-gray-500 hover:border-green-400 hover:text-green-600"
          >
            {totalPages}
          </button>
        </>
      )}

      <button
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors hover:border-green-400 hover:text-green-600"
      >
        <i className="ri-arrow-right-s-line text-lg"></i>
      </button>
    </div>
  );
};

const CustomerReviews = () => {
  const [desktopPage, setDesktopPage] = useState(1);
  const [mobilePage, setMobilePage] = useState(1);

  const desktopTotalPages = Math.ceil(reviews.length / DESKTOP_PAGE_SIZE);
  const mobileTotalPages = Math.ceil(reviews.length / MOBILE_PAGE_SIZE);

  const desktopReviews = reviews.slice(
    (desktopPage - 1) * DESKTOP_PAGE_SIZE,
    desktopPage * DESKTOP_PAGE_SIZE
  );
  const mobileReviews = reviews.slice(
    (mobilePage - 1) * MOBILE_PAGE_SIZE,
    mobilePage * MOBILE_PAGE_SIZE
  );

  // 데스크탑: 10개를 3열 masonry로
  const col1 = desktopReviews.filter((_, i) => i % 3 === 0);
  const col2 = desktopReviews.filter((_, i) => i % 3 === 1);
  const col3 = desktopReviews.filter((_, i) => i % 3 === 2);

  return (
    <section id="reviews" className="py-16 md:py-24 bg-gray-50/60">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-3">
            <i className="ri-chat-smile-2-line"></i>
            REVIEWS
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-3">
            고객님들의 생생한 후기
          </h2>
          <p className="text-gray-500 text-sm md:text-base">
            실제 구매 고객님들의 솔직한 이야기를 들어보세요
          </p>
          {/* Stats */}
          <div className="flex justify-center gap-8 mt-6">
            <div className="text-center">
              <div className="text-2xl font-extrabold text-gray-900">4.9</div>
              <div className="flex justify-center gap-0.5 my-1">
                {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-yellow-400 text-xs"></i>)}
              </div>
              <div className="text-xs text-gray-500">평균 평점</div>
            </div>
            <div className="w-px bg-gray-200"></div>
            <div className="text-center">
              <div className="text-2xl font-extrabold text-gray-900">50+</div>
              <div className="text-xs text-gray-500 mt-1">고객 후기</div>
            </div>
            <div className="w-px bg-gray-200"></div>
            <div className="text-center">
              <div className="text-2xl font-extrabold text-gray-900">98%</div>
              <div className="text-xs text-gray-500 mt-1">재구매율</div>
            </div>
          </div>
        </div>

        {/* Desktop: 3열 masonry + 페이지네이션 */}
        <div className="hidden md:block">
          <div className="grid grid-cols-3 gap-4">
            <div className="flex flex-col gap-4">
              {col1.map((r) => <ReviewCard key={r.id} review={r} />)}
            </div>
            <div className="flex flex-col gap-4">
              {col2.map((r) => <ReviewCard key={r.id} review={r} />)}
            </div>
            <div className="flex flex-col gap-4">
              {col3.map((r) => <ReviewCard key={r.id} review={r} />)}
            </div>
          </div>
          <Pagination
            currentPage={desktopPage}
            totalPages={desktopTotalPages}
            onPageChange={(p) => { setDesktopPage(p); }}
          />
          <p className="text-center text-xs text-gray-400 mt-3">
            {desktopPage} / {desktopTotalPages} 페이지 · 총 {reviews.length}개 후기
          </p>
        </div>

        {/* Mobile: 1열 + 페이지네이션 */}
        <div className="md:hidden">
          <div className="flex flex-col gap-4">
            {mobileReviews.map((r) => <ReviewCard key={r.id} review={r} />)}
          </div>
          <Pagination
            currentPage={mobilePage}
            totalPages={mobileTotalPages}
            onPageChange={(p) => { setMobilePage(p); }}
          />
          <p className="text-center text-xs text-gray-400 mt-3">
            {mobilePage} / {mobileTotalPages} 페이지 · 총 {reviews.length}개 후기
          </p>
        </div>
      </div>
    </section>
  );
};

export default CustomerReviews;
